import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import AngelsSlider from './AngelsSlider'
import digikore_logo from '../../images/digikore_logo.png'
import google_veo_logo from '../../images/partners_logo/Google_Veo_3_logo.png'
import eleven_labs_logo from '../../images/partners_logo/elevenlabs_logo.png'
import kling_ai_logo from '../../images/partners_logo/kling_ai_logo.png'
import runway_logo from '../../images/partners_logo/runway_logo.png'
import ContactForm from './ContactFrom'

const Content = ({ref}) => {
    const [displayText, setDisplayText] = useState('')
    const [isTyping, setIsTyping] = useState(true)
    const fullText = "THE ANGELS UNIVERSE"

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: "easeOut"
            }
        }
    }

    // Typing effect
    useEffect(() => {
        if (!isTyping) return

        let currentIndex = 0
        let timeoutId // no type here

        const typingSpeed = 240 // ms per character
        const pauseAtEnd = 9000 // ms to pause before restart

        const typeWriter = () => {
            if (currentIndex <= fullText.length) {
                setDisplayText(fullText.substring(0, currentIndex))
                currentIndex++
                timeoutId = setTimeout(typeWriter, typingSpeed)
            } else {
                timeoutId = setTimeout(() => {
                    setDisplayText('')
                    currentIndex = 0
                    typeWriter()
                }, pauseAtEnd)
            }
        }

        typeWriter()

        return () => {
            clearTimeout(timeoutId)
        }
    }, [isTyping, fullText])


    return (
        <>
            <motion.div
                className='min-h-screen bg-gradient-to-b from-black to-gray-900 pt-12 px-4'
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={containerVariants}
            >
                <div className='max-w-6xl mx-auto'>
                    {/* Typing Animation Section */}
                    <motion.div
                        className='text-center mb-2 pt-4'
                        variants={itemVariants}
                    >
                        <div className="relative inline-block">
                            {/* Typing Text */}
                            <motion.h1
                                className="text-6xl max-md:text-2xl font-bold bg-gradient-to-r from-white via-gray-300 to-white bg-clip-text text-transparent mb-2 pb-4"
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <h1
                                    className="
                                            font-extrabold
                                            text-white
                                            tracking-wide
                                            [text-shadow:0_0_16px_rgba(250,204,21,0.7)]
                                            [-webkit-text-stroke-width:2px]
                                            [-webkit-text-stroke-color:#B08E00]
                                            flex items-center gap-1
                                        "
                                >
                                    <span>{displayText}</span>
                                    <span className="w-[3px] h-8 md:h-10 bg-amber-500 animate-pulse" />
                                </h1>
                            </motion.h1>

                            {/* Glow Effect */}
                            <div className="absolute -inset-4 bg-gradient-to-r from-red-500/20 via-orange-500/20 to-red-500/20 blur-xl opacity-70 rounded-lg -z-10"></div>
                        </div>
                    </motion.div>

                    {/* Quote Section */}
                    <motion.div
                        className='text-center mb-2 pt-8'
                        variants={itemVariants}
                    >
                        <motion.i
                            className='text-xl md:text-4xl font-semibold text-gray-200 leading-relaxed block mb-8 italic'
                            whileHover={{ scale: 1.02 }}
                            transition={{ type: "spring", stiffness: 300 }}
                        >
                            "Not all angels have wings. Some have armor, fire and bad timing"
                        </motion.i>

                        <motion.div
                            className="w-24 h-1 bg-gradient-to-r from-red-600 to-orange-500 mx-auto mb-4"
                            initial={{ width: 0 }}
                            whileInView={{ width: 96 }}
                            transition={{ delay: 0.5, duration: 0.8 }}
                            viewport={{ once: true }}
                        />
                    </motion.div>

                    {/* Main Content */}
                    <motion.div
                        className='flex items-center'
                        variants={itemVariants}
                    >
                        <motion.div
                            whileHover={{ scale: 1.03 }}
                            transition={{ type: "spring", stiffness: 300 }}
                        >
                            <div className="p-2">
                                <p className="text-lg text-center text-gray-300 leading-relaxed">
                                    The Angels Universe is a new, interconnected live-action franchise built for a world on the edge. Ten
                                    grounded, diverse "angels" collide with time fractures, rogue AIs, corporate empires and multiversal threats—while
                                    dealing with very human flaws</p>
                            </div>
                        </motion.div>
                    </motion.div>

                </div>
                <AngelsSlider />
            </motion.div>

            {/* Rest of your component remains the same */}
            <motion.div
                className="w-full py-4 bg-gradient-to-b from-gray-900 to-black"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
            >
                <div className="max-w-7xl mx-auto px-4">
                    {/* What Is The Angels Universe Section */}
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-4xl md:text-5xl font-bold pb-6 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                            What Is The Angels Universe?
                        </h2>

                        <div className="max-w-6xl mx-auto">
                            <p className="text-xl text-gray-300 leading-relaxed mb-8">
                                The Angels Universe is a character-driven, superhero saga where every "angel" is a flawed protector.
                                Time, law, faith, code, fire, sound, probability and power collide in a single, shared continuity
                                designed for films, series and games
                            </p>

                            {/* Sub-line */}
                            <motion.div
                                className=" border-red-500 p-6 rounded-r-lg mb-8"
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.5, duration: 0.8 }}
                                viewport={{ once: true }}
                            >
                                <p className="text-lg italic text-gray-200">
                                    "Think Marvel/DC scale, but wired into today's headlines: AI, surveillance, cults, smart cities and broken systems"
                                </p>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Production Partner Section */}
                    <motion.div
                        className="text-center border-t border-gray-800 pt-16 mb-12 max-md:mb-8"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ delay: 0.7, duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h3 className="text-2xl font-semibold text-gray-200 mb-8">Production and Technology Partners</h3>

                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            transition={{ type: "spring", stiffness: 300 }}
                        >
                            <div className="max-w-4xl mx-auto">
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8 items-center justify-center">

                                    {/* Logo 1 - Digikore Studios */}
                                    <motion.div
                                        className="flex justify-center items-center"
                                        whileHover={{ scale: 1.1 }}
                                        transition={{ type: "spring", stiffness: 400 }}
                                    >
                                        <img
                                            src={digikore_logo}
                                            alt="Digikore Studios"
                                            className="h-6 md:h-8 w-auto filter brightness-0 invert opacity-90 hover:opacity-100 transition-opacity duration-300"
                                        />
                                    </motion.div>

                                    {/* Logo 2 - Google Veo */}
                                    <motion.div
                                        className="flex justify-center items-center"
                                        whileHover={{ scale: 1.1 }}
                                        transition={{ type: "spring", stiffness: 400 }}
                                    >
                                        <img
                                            src={google_veo_logo}
                                            alt="Google Veo"
                                            className="h-8 md:h-12 w-auto opacity-90 hover:opacity-100 transition-opacity duration-300"
                                        />
                                    </motion.div>

                                    {/* Logo 3 - Kling AI - This will be in the middle column on desktop */}
                                    <motion.div
                                        className="flex justify-center items-center col-span-2 md:col-span-1 order-last md:order-none"
                                        whileHover={{ scale: 1.1 }}
                                        transition={{ type: "spring", stiffness: 400 }}
                                    >
                                        <img
                                            src={kling_ai_logo}
                                            alt="Kling AI"
                                            className="h-12 md:h-16 w-auto opacity-90 hover:opacity-100 transition-opacity duration-300"
                                        />
                                    </motion.div>

                                    {/* Logo 4 - Eleven Labs */}
                                    <motion.div
                                        className="flex justify-center items-center"
                                        whileHover={{ scale: 1.1 }}
                                        transition={{ type: "spring", stiffness: 400 }}
                                    >
                                        <img
                                            src={eleven_labs_logo}
                                            alt="Eleven Labs"
                                            className="h-4 md:h-6 w-auto filter brightness-0 invert opacity-90 hover:opacity-100 transition-opacity duration-300"
                                        />
                                    </motion.div>

                                    {/* Logo 5 - Runway */}
                                    <motion.div
                                        className="flex justify-center items-center"
                                        whileHover={{ scale: 1.1 }}
                                        transition={{ type: "spring", stiffness: 400 }}
                                    >
                                        <img
                                            src={runway_logo}
                                            alt="Runway"
                                            className="h-6 md:h-8 w-auto filter brightness-0 invert opacity-90 hover:opacity-100 transition-opacity duration-300"
                                        />
                                    </motion.div>

                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.div>

            <div>
                <ContactForm ref={ref} />
            </div>
        </>
    )
}

export default Content